import * as styles from "../styles";
import { useColumnFilter } from "../utils/columnSelectorUtils";
import {
  extractVarColumns,
  normalizeDataType,
} from "../utils/expressionEditorUtils";
import { withUniqueColumnNames } from "../utils/specBuilderSupport";
import ColumnFilterBar, { TYPE_COLORS } from "./ColumnFilterBar";
import DataTypeIcon from "./DataTypeIcon";
import { AddAggregateDialog } from "./Dialogs";
import {
  MonetizationOn as MoneyIcon,
  Event as DateIcon,
  Numbers as UnitIcon,
  Percent as PercentIcon,
  Sell as StringIcon,
  Discount as EnumIcon,
  LibraryAddCheck as BooleanIcon,
  Functions as CalculatedIcon,
  FilterAlt as FilterAltIcon,
} from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Button from "components/common/Button";
import * as labels from "config.labels";
import { ENABLE_AGGREGATE_COLUMN_GENERATION } from "flags";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDrag, useDrop } from "react-dnd";
import * as staticDataService from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";

export const COLUMN_DATA_TYPE_ICONS = {
  date: DateIcon,
  binary: BooleanIcon,
  currency: MoneyIcon,
  percentage: PercentIcon,
  unit: UnitIcon,
  text: StringIcon,
  option: EnumIcon,
  aggregate: CalculatedIcon,
  groupAggregate: CalculatedIcon,
};

const DRAG_TYPE = "COLUMN";

const SX_SECTION_HEADING = { fontSize: "1.2rem", fontWeight: 400 };
const SX_LIST_OUTER = { height: 400, mt: "auto" };
const getSxListBorder = (theme) => ({
  borderRadius: 2,
  border: `1px solid ${theme.brand.gray.gainsboro}`,
});
const SX_CENTERED_TEXT = { textAlign: "center", py: 2 };
const getDropIndicatorSx = (theme) => ({
  position: "absolute",
  left: 0,
  right: 0,
  height: 2,
  backgroundColor: theme.brand.accent[500],
  borderRadius: 1,
  zIndex: 10,
});

const columnKey = (col) =>
  col.sourceTable
    ? `${col.sourceTable}:${col.internalName ?? col.column}`
    : col.internalName ?? col.column;

const ColumnChip = React.forwardRef(
  (
    {
      column,
      onClick,
      label,
      disableInternalDrag = false,
      isExternallyDragging,
      dimmed = false,
    },
    forwardedRef
  ) => {
    const internalRef = useRef(null);
    const combinedRef = useCallback(
      (node) => {
        internalRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef]
    );
    const [{ isDragging }, drag] = useDrag(
      () => ({
        type: DRAG_TYPE,
        canDrag: () => !disableInternalDrag,
        item: {
          type: "available",
          column: column.internalName,
          sourceTable: column.sourceTable,
          displayName: column.displayName,
        },
        collect: (monitor) => ({
          isDragging: monitor.isDragging(),
        }),
      }),
      [column, disableInternalDrag]
    );
    if (!disableInternalDrag) {
      drag(internalRef);
    }
    const effectiveIsDragging =
      isExternallyDragging !== undefined ? isExternallyDragging : isDragging;
    const colors = TYPE_COLORS[normalizeDataType(column.dataType)];
    const displayName = label ?? column.displayName;
    return (
      <Box
        ref={combinedRef}
        onClick={onClick}
        aria-label={displayName}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "0.5em",
          borderRadius: 1,
          backgroundColor: "transparent",
          border: "1px solid transparent",
          color: "text.primary",
          fontSize: "0.875rem",
          fontWeight: 400,
          cursor: "grab",
          transition: "all 0.15s ease",
          userSelect: "none",
          opacity: effectiveIsDragging ? 0.4 : dimmed ? 0.45 : 1,
          width: "100%",
          "&:hover": {
            backgroundColor: "action.hover",
            borderColor: "divider",
          },
          "&:active": {
            cursor: "grabbing",
          },
          gap: 1,
        }}
      >
        <Box
          sx={{
            color: colors.text,
            display: "flex",
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          <DataTypeIcon type={normalizeDataType(column.dataType)} />
        </Box>
        <Box sx={{ pointerEvents: "none" }}>{displayName}</Box>
      </Box>
    );
  }
);
ColumnChip.displayName = "ColumnChip";

const OutputPill = ({
  column,
  index,
  columnDefinition,
  isDimmed,
  notForExport,
  onRemove,
  onEdit,
  onToggleNotForExport,
  label,
  hasPreAggFilter,
  onPreAggFilterClick,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DRAG_TYPE,
      item: {
        type: "output",
        column: column.column,
        sourceTable: column.sourceTable,
        index,
        displayName: column.displayName,
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [column, index]
  );

  drag(ref);

  const handleClick = useCallback(() => {
    if (onEdit) {
      onEdit();
    } else if (onRemove) {
      onRemove();
    }
  }, [onEdit, onRemove]);
  const pillLabel = label || column.displayName || columnDefinition.displayName;
  const includeInExcelLabel = translateLabels(
    labels.COLUMNS_SELECTOR_INCLUDE_IN_EXCEL,
    { default: "Include in Excel" }
  );
  const filterRowsLabel = translateLabels(labels.REPORTS_V3_FILTER_KIND_ROWS, {
    default: "Filter rows (before grouping)",
  });

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Tooltip title={includeInExcelLabel} describeChild>
        <Checkbox
          size={"small"}
          inputProps={{ "aria-label": `${includeInExcelLabel}: ${pillLabel}` }}
          checked={!Boolean(notForExport)}
          onChange={(e) => {
            e.stopPropagation();
            onToggleNotForExport?.();
          }}
          onClick={(e) => e.stopPropagation()}
          sx={{ p: 0.25, flexShrink: 0 }}
        />
      </Tooltip>
      <ColumnChip
        ref={ref}
        column={columnDefinition}
        label={pillLabel}
        onClick={handleClick}
        disableInternalDrag
        isExternallyDragging={isDragging}
        dimmed={isDimmed}
      />
      {onPreAggFilterClick && (
        <Tooltip title={filterRowsLabel}>
          <IconButton
            size={"small"}
            aria-label={`${filterRowsLabel}: ${pillLabel}`}
            onClick={(e) => {
              e.stopPropagation();
              onPreAggFilterClick(column.column, e.currentTarget);
            }}
            sx={{
              ml: 0.25,
              flexShrink: 0,
              color: hasPreAggFilter ? "primary.main" : "action.disabled",
            }}
          >
            <FilterAltIcon fontSize={"small"} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const AvailableColumnsArea = ({ children, onRemoveColumn }) => {
  const theme = useTheme();
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const ref = useRef(null);
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DRAG_TYPE,
      canDrop: (item) => item.type === "output",
      drop: (item) => {
        if (item.type === "output") {
          onRemoveColumn(item.column, item.sourceTable);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [onRemoveColumn]
  );
  drop(ref);
  const showHighlight = isOver && canDrop;
  return (
    <Box
      ref={ref}
      sx={{
        backgroundColor: showHighlight
          ? `${theme.brand.accent[500]}14`
          : "transparent",
        border: showHighlight
          ? `2px dashed ${theme.brand.accent[500]}`
          : "2px dashed transparent",
        borderRadius: 2,
        transition: "all 0.15s ease",
        p: 1,
        m: -1,
        mt: "auto",
      }}
    >
      {children}
      {showHighlight && (
        <Typography
          variant={"caption"}
          color={"primary"}
          sx={{
            display: "block",
            mt: 1,
            textAlign: "center",
          }}
        >
          {translateLabels(labels.COLUMNS_SELECTOR_DROP_TO_REMOVE, {
            default: "Drop here to remove",
          })}
        </Typography>
      )}
    </Box>
  );
};

const UnifiedColumnSelectorInner = ({
  availableColumns,
  outputColumns,
  onOutputChange,
  isLoading,
  isMultiTable = false,
  tableDisplayNameMap = {},
  onDeleteExtraColumn,
  preAggFilterCols,
  onPreAggFilterClick,
}) => {
  const aggregationEnabled = staticDataService.useFeatureFlag(
    ENABLE_AGGREGATE_COLUMN_GENERATION,
    { default: true }
  );
  const theme = useTheme();
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const [dropIndicatorPosition, setDropIndicatorPosition] = useState(null);
  const [addAggregateOpen, setAddAggregateOpen] = useState(false);
  const [editingAggregateIndex, setEditingAggregateIndex] = useState(null);
  const [cascadeWarning, setCascadeWarning] = useState(null);
  const [groupAggDeleteConfirm, setGroupAggDeleteConfirm] = useState(null);
  const availableScrollRef = useRef(null);

  const handleEditAggregate = useCallback((index) => {
    setEditingAggregateIndex(index);
    setAddAggregateOpen(true);
  }, []);

  const handleAggregateDialogClose = useCallback(() => {
    setAddAggregateOpen(false);
    setEditingAggregateIndex(null);
  }, []);

  const handleDeleteAggregate = useCallback(() => {
    if (editingAggregateIndex === null) return;
    onOutputChange(outputColumns.filter((_, i) => i !== editingAggregateIndex));
    setAddAggregateOpen(false);
    setEditingAggregateIndex(null);
  }, [editingAggregateIndex, outputColumns, onOutputChange]);

  const handleAddAggregate = useCallback(
    ({ columnName, expression }) => {
      const requiredColumns = [...new Set(extractVarColumns(expression))];
      const outputColumnNames = new Set(outputColumns.map((c) => c.column));
      const newRequiredEntries = requiredColumns
        .filter((name) => !outputColumnNames.has(name))
        .flatMap((name) => {
          const def = availableColumns.find((c) => c.internalName === name);
          if (!def) return [];
          return [
            {
              column: name,
              sourceTable: def.sourceTable,
              displayName: def.displayName,
              notForExport: true,
            },
          ];
        });

      if (editingAggregateIndex !== null) {
        const updated = outputColumns.map((col, i) =>
          i === editingAggregateIndex
            ? {
                ...col,
                column: columnName,
                displayName: columnName,
                expression,
                requiredColumns,
              }
            : col
        );
        onOutputChange([...updated, ...newRequiredEntries]);
      } else {
        onOutputChange([
          ...outputColumns,
          ...newRequiredEntries,
          {
            column: columnName,
            displayName: columnName,
            dataType: "aggregate",
            expression,
            requiredColumns,
          },
        ]);
      }
      setAddAggregateOpen(false);
      setEditingAggregateIndex(null);
    },
    [outputColumns, onOutputChange, editingAggregateIndex, availableColumns]
  );

  const columnLabel = useCallback(
    (col) => {
      const name = col.displayName;
      if (!isMultiTable) return name;
      const tableName =
        tableDisplayNameMap[col.sourceTable ?? col.column] ??
        col.sourceTable ??
        "";
      return tableName ? `${name} - ${tableName}` : name;
    },
    [isMultiTable, tableDisplayNameMap]
  );

  const availableColumnsForEditor = useMemo(
    () =>
      availableColumns.map((col) => ({
        ...col,
        displayName: columnLabel(col),
      })),
    [availableColumns, columnLabel]
  );

  const remainingColumns = useMemo(() => {
    const outputKeys = new Set(outputColumns.map((c) => columnKey(c)));
    return availableColumns.filter((c) => !outputKeys.has(columnKey(c)));
  }, [availableColumns, outputColumns]);

  const availableMatchesText = useCallback(
    (col, lower) =>
      columnLabel(col).toLowerCase().includes(lower) ||
      col.internalName.toLowerCase().includes(lower),
    [columnLabel]
  );

  const availableFilter = useColumnFilter(remainingColumns, {
    matchesText: availableMatchesText,
  });

  const visibleColumns = useMemo(
    () =>
      [...availableFilter.filteredColumns].sort((a, b) =>
        columnLabel(a).localeCompare(columnLabel(b))
      ),
    [availableFilter.filteredColumns, columnLabel]
  );

  const handleAddColumn = useCallback(
    (columnName, sourceTable) => {
      const def = availableColumns.find(
        (c) =>
          c.internalName === columnName &&
          (!sourceTable || c.sourceTable === sourceTable)
      );
      if (!def) return;
      const newOutput = [
        ...outputColumns,
        {
          column: columnName,
          sourceTable: def.sourceTable,
          displayName: def.displayName,
        },
      ];
      onOutputChange(newOutput);
    },
    [availableColumns, outputColumns, onOutputChange]
  );

  const minDropPosition = 0;

  const handleAddColumnAtIndex = useCallback(
    (columnName, sourceTable, index) => {
      const def = availableColumns.find(
        (c) =>
          c.internalName === columnName &&
          (!sourceTable || c.sourceTable === sourceTable)
      );
      if (!def) return;

      const effectiveIndex = Math.max(index, minDropPosition);
      const newOutput = [...outputColumns];
      newOutput.splice(effectiveIndex, 0, {
        column: columnName,
        sourceTable: def.sourceTable,
        displayName: def.displayName,
      });
      onOutputChange(newOutput);
    },
    [availableColumns, outputColumns, onOutputChange, minDropPosition]
  );

  const requiredByAggregate = useMemo(() => {
    const names = new Set();
    outputColumns.forEach((col) => {
      if (col.dataType === "aggregate" && col.requiredColumns) {
        col.requiredColumns.forEach((name) => names.add(name));
      }
    });
    return names;
  }, [outputColumns]);

  const handleRemoveColumn = useCallback(
    (columnName, sourceTable) => {
      const col = outputColumns.find(
        (c) =>
          c.column === columnName &&
          (!sourceTable || c.sourceTable === sourceTable)
      );
      if (col?.dataType === "groupAggregate") {
        setGroupAggDeleteConfirm({ columnName, sourceTable });
        return;
      }
      if (requiredByAggregate.has(columnName)) return;
      const affected = outputColumns.filter(
        (c) => c.dataType === "groupAggregate" && c.sourceColumn === columnName
      );
      if (affected.length > 0) {
        const displayName = col?.displayName ?? columnName;
        setCascadeWarning({ columnName, sourceTable, displayName, affected });
        return;
      }
      onOutputChange(
        outputColumns.filter(
          (c) =>
            !(
              c.column === columnName &&
              (!sourceTable || c.sourceTable === sourceTable)
            )
        )
      );
    },
    [outputColumns, onOutputChange, requiredByAggregate]
  );

  const handleCascadeConfirm = useCallback(() => {
    if (!cascadeWarning) return;
    const { columnName, sourceTable, affected } = cascadeWarning;
    const affectedNames = new Set(affected.map((e) => e.column));
    affected.forEach((e) => onDeleteExtraColumn?.(e.column));
    onOutputChange(
      outputColumns.filter(
        (c) =>
          !affectedNames.has(c.column) &&
          !(
            c.column === columnName &&
            (!sourceTable || c.sourceTable === sourceTable)
          )
      )
    );
    setCascadeWarning(null);
  }, [cascadeWarning, onDeleteExtraColumn, onOutputChange, outputColumns]);

  const handleMoveToIndex = useCallback(
    (fromIndex, toIndex) => {
      const effectiveToIndex = Math.max(toIndex, minDropPosition);
      const newOutput = [...outputColumns];
      const [removed] = newOutput.splice(fromIndex, 1);
      if (removed) {
        const adjustedIndex =
          fromIndex < effectiveToIndex
            ? effectiveToIndex - 1
            : effectiveToIndex;
        newOutput.splice(adjustedIndex, 0, removed);
        onOutputChange(newOutput);
      }
    },
    [outputColumns, onOutputChange, minDropPosition]
  );

  const handleDropOnPill = useCallback(
    (item, targetIndex) => {
      if (item.type === "available") {
        handleAddColumnAtIndex(item.column, item.sourceTable, targetIndex);
      }
    },
    [handleAddColumnAtIndex]
  );

  const handleAddAll = useCallback(() => {
    const newColumns = visibleColumns.map((col) => ({
      column: col.internalName,
      sourceTable: col.sourceTable,
      displayName: col.displayName,
    }));
    onOutputChange([...outputColumns, ...newColumns]);
    availableFilter.setFilterText("");
    availableFilter.handleSelectAnyMode();
  }, [visibleColumns, outputColumns, onOutputChange, availableFilter]);

  const handleToggleNotForExport = useCallback(
    (index) => {
      const updated = outputColumns.map((col, i) =>
        i === index ? { ...col, notForExport: !col.notForExport } : col
      );
      onOutputChange(updated);
    },
    [outputColumns, onOutputChange]
  );

  const handleClearAll = useCallback(() => {
    onOutputChange([]);
  }, [onOutputChange]);

  const removableColumnCount = outputColumns.length;

  const outputColumnsWithDefs = useMemo(() => {
    return outputColumns.map((col) => {
      if (col.dataType === "aggregate" || col.dataType === "groupAggregate") {
        return {
          col,
          def: {
            internalName: col.column,
            displayName: col.displayName,
            dataType: col.dataType,
          },
        };
      }
      const def = availableColumns.find(
        (c) =>
          c.internalName === col.column &&
          (!col.sourceTable || c.sourceTable === col.sourceTable)
      );
      return { col, def };
    });
  }, [outputColumns, availableColumns]);

  const outputGetDataType = useCallback(({ def }) => def?.dataType ?? "", []);
  const outputMatchesText = useCallback(
    ({ col, def }, lower) =>
      !!def &&
      (columnLabel({ ...def, displayName: col.displayName ?? def.displayName })
        .toLowerCase()
        .includes(lower) ||
        (col.column ?? "").toLowerCase().includes(lower)),
    [columnLabel]
  );

  const outputFilter = useColumnFilter(outputColumnsWithDefs, {
    getDataType: outputGetDataType,
    matchesText: outputMatchesText,
  });

  const filteredOutputIndices = useMemo(() => {
    const filteredSet = new Set(outputFilter.filteredColumns);
    return new Set(
      outputColumnsWithDefs
        .map((item, i) => (filteredSet.has(item) ? i : -1))
        .filter((i) => i >= 0)
    );
  }, [outputColumnsWithDefs, outputFilter.filteredColumns]);

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color={"text.secondary"}>
          {translateLabels(labels.COLUMNS_SELECTOR_LOADING, {
            default: "Loading columns...",
          })}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", gap: 4 }}>
      {/* Left Column - Available Columns */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={styles.flexBetween}>
          <Typography
            variant={"subtitle2"}
            color={"text.secondary"}
            sx={SX_SECTION_HEADING}
          >
            {translateLabels(labels.COLUMNS_SELECTOR_SELECT_COLUMNS, {
              default: "Select Columns",
            })}
          </Typography>
          <Button
            onClick={handleAddAll}
            isDisabled={visibleColumns.length === 0}
          >
            {`${translateLabels(labels.COLUMNS_SELECTOR_ADD_ALL, {
              default: "ADD ALL",
            })} (${visibleColumns.length})`}
          </Button>
        </Box>
        <ColumnFilterBar
          filterText={availableFilter.filterText}
          onFilterTextChange={availableFilter.setFilterText}
          selectedDisplayTypes={availableFilter.selectedDisplayTypes}
          onToggleType={availableFilter.handleToggleType}
          onSelectAnyMode={availableFilter.handleSelectAnyMode}
          isAnyTypeMode={availableFilter.isAnyTypeMode}
          typeCounts={availableFilter.typeCounts}
          totalCount={remainingColumns.length}
          filteredCount={visibleColumns.length}
          placeholder={translateLabels(
            labels.COLUMNS_SELECTOR_SEARCH_AVAILABLE,
            {
              default: "columns",
            }
          )}
        />
        <AvailableColumnsArea onRemoveColumn={handleRemoveColumn}>
          <Box sx={SX_LIST_OUTER}>
            <Box
              sx={{
                position: "relative",
                height: "100%",
                ...getSxListBorder(theme),
                overflowY: "auto",
              }}
              ref={availableScrollRef}
            >
              <Box sx={{ p: 1 }}>
                {visibleColumns.map((column) => (
                  <ColumnChip
                    key={columnKey(column)}
                    column={column}
                    label={columnLabel(column)}
                    onClick={() =>
                      handleAddColumn(column.internalName, column.sourceTable)
                    }
                  />
                ))}
                {visibleColumns.length === 0 && (
                  <Typography
                    variant={"body2"}
                    color={"text.secondary"}
                    sx={SX_CENTERED_TEXT}
                  >
                    {remainingColumns.length === 0
                      ? translateLabels(labels.COLUMNS_SELECTOR_ALL_ADDED, {
                          default: "All columns have been added",
                        })
                      : translateLabels(labels.COLUMNS_SELECTOR_NO_MATCH, {
                          default: "No columns match your filters",
                        })}
                  </Typography>
                )}
              </Box>
            </Box>
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ mt: 0.5 }}
            >
              {translateLabels(labels.COLUMNS_SELECTOR_CLICK_TO_ADD, {
                default:
                  "Click to add \u00b7 Drag to insert into Report Column",
              })}
            </Typography>
          </Box>
        </AvailableColumnsArea>
      </Box>

      {/* Right Column - Report Columns */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={styles.flexBetween}>
          <Typography
            variant={"subtitle2"}
            color={"text.secondary"}
            sx={SX_SECTION_HEADING}
          >
            {translateLabels(labels.COLUMNS_SELECTOR_REPORT_COLUMNS, {
              default: "Report Columns",
            })}
          </Typography>
          {aggregationEnabled && (
            <Button
              onClick={() => setAddAggregateOpen(true)}
              sx={{ mx: "4px" }}
            >
              {"ADD Function"}
            </Button>
          )}
          <Button
            onClick={handleClearAll}
            isDisabled={removableColumnCount === 0}
          >
            {`${translateLabels(labels.COLUMNS_SELECTOR_CLEAR_ALL, {
              default: "CLEAR",
            })} (${removableColumnCount})`}
          </Button>
        </Box>
        <AddAggregateDialog
          open={addAggregateOpen}
          onClose={handleAggregateDialogClose}
          onSave={handleAddAggregate}
          onDelete={
            editingAggregateIndex !== null ? handleDeleteAggregate : undefined
          }
          availableColumns={availableColumnsForEditor}
          existingAggregateColumns={outputColumns.filter(
            (c) => c.dataType === "aggregate"
          )}
          initialColumnName={
            editingAggregateIndex !== null
              ? outputColumns[editingAggregateIndex]?.displayName
              : undefined
          }
          initialExpression={
            editingAggregateIndex !== null
              ? outputColumns[editingAggregateIndex]?.expression
              : undefined
          }
        />
        <ColumnFilterBar
          filterText={outputFilter.filterText}
          onFilterTextChange={outputFilter.setFilterText}
          selectedDisplayTypes={outputFilter.selectedDisplayTypes}
          onToggleType={outputFilter.handleToggleType}
          onSelectAnyMode={outputFilter.handleSelectAnyMode}
          isAnyTypeMode={outputFilter.isAnyTypeMode}
          typeCounts={outputFilter.typeCounts}
          totalCount={outputColumns.length}
          filteredCount={filteredOutputIndices.size}
          placeholder={translateLabels(labels.COLUMNS_SELECTOR_SEARCH_OUTPUT, {
            default: "report columns",
          })}
        />
        <OutputColumnsContainer
          outputColumns={outputColumns}
          outputColumnsWithDefs={outputColumnsWithDefs}
          dropIndicatorPosition={dropIndicatorPosition}
          setDropIndicatorPosition={setDropIndicatorPosition}
          handleMoveToIndex={handleMoveToIndex}
          handleDropOnPill={handleDropOnPill}
          onRemoveColumn={handleRemoveColumn}
          onEditAggregate={handleEditAggregate}
          onToggleNotForExport={handleToggleNotForExport}
          columnLabel={columnLabel}
          filteredIndices={filteredOutputIndices}
          requiredByAggregate={requiredByAggregate}
          preAggFilterCols={preAggFilterCols}
          onPreAggFilterClick={onPreAggFilterClick}
        />
        <Dialog
          open={groupAggDeleteConfirm !== null}
          onClose={() => setGroupAggDeleteConfirm(null)}
        >
          <DialogTitle>
            {translateLabels(labels.COLUMNS_SELECTOR_GROUP_AGG_DELETE_TITLE, {
              default: "Delete column",
            })}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {translateLabels(labels.COLUMNS_SELECTOR_GROUP_AGG_DELETE_BODY, {
                default:
                  "This will delete the column, as it is a calculated column it will not be recoverable",
              })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGroupAggDeleteConfirm(null)}>
              {translateLabels(labels.REPORT_BUILDER_CANCEL, {
                default: "Cancel",
              })}
            </Button>
            <Button
              color={"error"}
              onClick={() => {
                if (!groupAggDeleteConfirm) return;
                const { columnName } = groupAggDeleteConfirm;
                onDeleteExtraColumn?.(columnName);
                onOutputChange(
                  outputColumns.filter((c) => c.column !== columnName)
                );
                setGroupAggDeleteConfirm(null);
              }}
            >
              {translateLabels(
                labels.COLUMNS_SELECTOR_GROUP_AGG_DELETE_CONFIRM,
                { default: "Delete" }
              )}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={cascadeWarning !== null}
          onClose={() => setCascadeWarning(null)}
        >
          <DialogTitle>{"Remove column"}</DialogTitle>
          <DialogContent>
            <Typography>
              {`Deleting "${cascadeWarning?.displayName}" will also remove:`}
            </Typography>
            <Box component={"ul"} sx={{ mt: 1, pl: 2 }}>
              {cascadeWarning?.affected.map((e) => (
                <Box
                  component={"li"}
                  key={e.column}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {e.column}
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCascadeWarning(null)}>{"Cancel"}</Button>
            <Button onClick={handleCascadeConfirm}>{"Remove all"}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

const OutputColumnsContainer = ({
  outputColumns,
  outputColumnsWithDefs,
  onEditAggregate,
  dropIndicatorPosition,
  setDropIndicatorPosition,
  handleMoveToIndex,
  handleDropOnPill,
  onRemoveColumn,
  onToggleNotForExport,
  columnLabel,
  filteredIndices,
  requiredByAggregate,
  preAggFilterCols,
  onPreAggFilterClick,
}) => {
  const theme = useTheme();
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const containerRef = useRef(null);
  const [highlightedRowIndices, setHighlightedRowIndices] = useState(null);

  const minDropPosition = 0;
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: DRAG_TYPE,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      hover: (item, monitor) => {
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset || !containerRef.current) {
          setDropIndicatorPosition(null);
          setHighlightedRowIndices(null);
          return;
        }
        const mouseY = clientOffset.y;
        const pillElements = containerRef.current.querySelectorAll(
          "[data-pill-index]"
        );
        if (!pillElements || pillElements.length === 0) {
          setDropIndicatorPosition(Math.max(0, minDropPosition));
          setHighlightedRowIndices(null);
          return;
        }
        const pills = [];
        const draggedIndex = item.type === "output" ? item.index : undefined;
        pillElements.forEach((pill) => {
          if (!pill) return;
          const pillIndex = parseInt(
            pill.getAttribute("data-pill-index") || "0",
            10
          );
          if (pillIndex === draggedIndex) return;
          const rect = pill.getBoundingClientRect();
          pills.push({
            index: pillIndex,
            centerY: rect.top + rect.height / 2,
          });
        });
        if (pills.length === 0) {
          setDropIndicatorPosition(Math.max(0, minDropPosition));
          setHighlightedRowIndices(null);
          return;
        }
        pills.sort((a, b) => a.centerY - b.centerY);

        let insertPosition = minDropPosition;
        for (let i = 0; i < pills.length; i++) {
          const pill = pills[i];
          if (!pill) continue;
          if (mouseY < pill.centerY) {
            insertPosition = pill.index;
            break;
          }
          insertPosition = pill.index + 1;
        }

        insertPosition = Math.max(insertPosition, minDropPosition);
        if (item.type === "output" && item.index !== undefined) {
          if (
            insertPosition === item.index ||
            insertPosition === item.index + 1
          ) {
            setDropIndicatorPosition(null);
            setHighlightedRowIndices(null);
            return;
          }
        }
        setDropIndicatorPosition(insertPosition);
        setHighlightedRowIndices(null);
      },
      drop: (item) => {
        if (dropIndicatorPosition === null) {
          setDropIndicatorPosition(null);
          setHighlightedRowIndices(null);
          return;
        }

        const insertPosition = Math.max(dropIndicatorPosition, minDropPosition);
        if (item.type === "available") {
          handleDropOnPill(item, insertPosition);
        } else if (item.type === "output" && item.index !== undefined) {
          handleMoveToIndex(item.index, insertPosition);
        }
        setDropIndicatorPosition(null);
        setHighlightedRowIndices(null);
      },
    }),
    [
      setDropIndicatorPosition,
      dropIndicatorPosition,
      handleDropOnPill,
      handleMoveToIndex,
    ]
  );
  drop(containerRef);
  // The name each column ends up with in the report, which is what a
  // pre-aggregation filter is held against: two sources can contribute a
  // column of the same name, and the second becomes "<name>_2".
  const filterColumnNames = useMemo(
    () => withUniqueColumnNames(outputColumns).map((col) => col.column),
    [outputColumns]
  );
  useEffect(() => {
    if (!isOver) {
      setDropIndicatorPosition(null);
      setHighlightedRowIndices(null);
    }
  }, [isOver, setDropIndicatorPosition]);
  return (
    <Box sx={SX_LIST_OUTER}>
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: 400,
          overflowY: "auto",
          p: 2,
          backgroundColor: isOver ? "rgba(250, 123, 53, 0.05)" : "#fafafa",
          ...getSxListBorder(theme),
        }}
      >
        {outputColumns.map((col, index) => {
          if (filteredIndices && !filteredIndices.has(index)) return null;
          const colDef = outputColumnsWithDefs?.[index]?.def;
          if (!colDef) return null;
          const isDimmed =
            highlightedRowIndices !== null && !highlightedRowIndices.has(index);
          return (
            <OutputPillWithDropZone
              key={columnKey(col)}
              column={col}
              index={index}
              columnDefinition={colDef}
              dropIndicatorPosition={dropIndicatorPosition}
              isDimmed={isDimmed}
              notForExport={Boolean(col.notForExport)}
              onRemove={
                col.dataType === "aggregate" ||
                requiredByAggregate?.has(col.column)
                  ? undefined
                  : () => onRemoveColumn(col.column, col.sourceTable)
              }
              onEdit={
                col.dataType === "aggregate"
                  ? () => onEditAggregate(index)
                  : undefined
              }
              onToggleNotForExport={() => onToggleNotForExport(index)}
              label={columnLabel({
                ...colDef,
                displayName: col.displayName ?? colDef.displayName,
              })}
              hasPreAggFilter={
                preAggFilterCols?.has(filterColumnNames[index]) ?? false
              }
              onPreAggFilterClick={
                col.dataType !== "groupAggregate"
                  ? (_column, anchorEl) =>
                      onPreAggFilterClick(filterColumnNames[index], anchorEl)
                  : undefined
              }
            />
          );
        })}
        {outputColumns.length === 0 && (
          <Typography
            variant={"body2"}
            color={"text.secondary"}
            sx={SX_CENTERED_TEXT}
          >
            {translateLabels(labels.COLUMNS_SELECTOR_DROP_HERE, {
              default: "Drag columns here",
            })}
          </Typography>
        )}
      </Box>
      <Typography
        variant={"caption"}
        color={"text.secondary"}
        sx={{
          mt: 0.5,
          display: "block",
        }}
      >
        {translateLabels(labels.COLUMNS_SELECTOR_CLICK_TO_REMOVE, {
          default:
            "Click to remove (Except columns used by aggregation) \u00b7 Drag to reorder",
        })}
      </Typography>
    </Box>
  );
};

const OutputPillWithDropZone = ({
  column,
  index,
  columnDefinition,
  dropIndicatorPosition,
  isDimmed,
  notForExport,
  onRemove,
  onEdit,
  onToggleNotForExport,
  label,
  hasPreAggFilter,
  onPreAggFilterClick,
}) => {
  const theme = useTheme();
  const showIndicatorBefore = dropIndicatorPosition === index;
  const showIndicatorAfter = dropIndicatorPosition === index + 1;
  const dropIndicatorSx = getDropIndicatorSx(theme);
  return (
    <Box
      data-pill-index={index}
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {showIndicatorBefore && <Box sx={{ ...dropIndicatorSx, top: -4 }} />}
      <OutputPill
        column={column}
        index={index}
        columnDefinition={columnDefinition}
        isDimmed={isDimmed}
        notForExport={notForExport}
        onRemove={onRemove}
        onEdit={onEdit}
        onToggleNotForExport={onToggleNotForExport}
        label={label}
        hasPreAggFilter={hasPreAggFilter}
        onPreAggFilterClick={onPreAggFilterClick}
      />
      {showIndicatorAfter && <Box sx={{ ...dropIndicatorSx, bottom: -4 }} />}
    </Box>
  );
};

const UnifiedColumnSelector = (props) => (
  <UnifiedColumnSelectorInner {...props} />
);
export default UnifiedColumnSelector;
